import {TimeFormat, toFrames} from '@accurate-player/accurate-player-core';
import {getQCJobMediaTimedText} from '@pluto/rest';
import {
    QcJobItemMediaTimedText,
    TimedText,
} from '@pluto/rest/lib/qc-types';
import {
    createAction,
    Action,
    ActionFunction1,
    ActionFunction2,
} from 'redux-actions';
import {Video} from 'VideoAudioQC/QCMedia/Redux/QCMediaActions';
import wrap from 'wraptile';
import {v4 as uuidv4} from 'uuid';
import {
    TimelineMarker,
    TimelineRow,
    TimelineRowType,
} from '../../../Timeline/TimelineTypes';

export const SUBTITLE_SET = 'SUBTITLE_SET';
export const SUBTITLES_RESET = 'SUBTITLES_RESET';
export const SUBTITLE_ENABLE = 'SUBTITLE_ENABLE';
export const SUBTITLE_DISABLE = 'SUBTITLE_DISABLE';
export const SUBTITLE_DISABLE_ALL = 'SUBTITLE_DISABLE_ALL';

export const parseSubtitleMap = wrap((video: Video, subtitle: QcJobItemMediaTimedText): TimelineMarker => {
    const {
        frameRate,
        dropFrame,
        duration,
    } = video;
    const {
        numerator,
        denominator,
    } = frameRate;
    const frameRateValue = numerator / denominator;
    
    const {
        start,
        value,
    } = subtitle;
    const subtitleDuration = subtitle.duration;
    const end = start + subtitleDuration;
    
    const startFrame = toFrames(
        start,
        TimeFormat.Milliseconds,
        frameRateValue,
        dropFrame,
        duration,
    );
    const endFrame = toFrames(
        end,
        TimeFormat.Milliseconds,
        frameRateValue,
        dropFrame,
        duration,
    );
    const identifier = uuidv4();
    
    return {
        identifier,
        start: startFrame,
        end: endFrame,
        text: value,
    };
});

export const parseSubtitles = (subtitles: QcJobItemMediaTimedText[], video: Video): TimelineMarker[] => {
    const handleParseSubtitleMap = parseSubtitleMap(video);
    
    return subtitles.map(handleParseSubtitleMap);
};

export const parseTimedText = (timedText: TimedText, video: Video, subtitles: QcJobItemMediaTimedText[]): TimelineRow => {
    const {
        timedTextUrl,
        language,
    } = timedText;
    const identifier = timedTextUrl;
    const label = language;
    
    return {
        identifier,
        label,
        type: TimelineRowType.Subtitle,
        markers: parseSubtitles(subtitles, video),
    };
};

export type SubtitlePayload = TimelineRow | null;

export const setSubtitleAction = createAction(SUBTITLE_SET, async (timedText: TimedText, video: Video): Promise<SubtitlePayload> => {
    try {
        const subtitle = await getQCJobMediaTimedText(timedText.jsonUrl);
        
        return parseTimedText(timedText, video, subtitle);
    } catch(error) {
        console.error(error);
        return null;
    }
});

export const enableSubtitleAction = createAction<string>(SUBTITLE_ENABLE);

export const disableSubtitleAction = createAction<string>(SUBTITLE_DISABLE);

export const disableAllSubtitlesAction = createAction<void>(SUBTITLE_DISABLE_ALL);

export const resetSubtitlesAction = createAction<void>(SUBTITLES_RESET);

export type SetSubtitle = ActionFunction2<TimedText, Video, Action<Promise<SubtitlePayload>>>;
export type EnableSubtitle = ActionFunction1<string, Action<string>>;
export type DisableSubtitle = ActionFunction1<string, Action<string>>;
export type DisableAllSubtitles = ActionFunction1<void, Action<void>>;
